import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PendingDeliveries = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  
  // Filter states
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [projectFilter, setProjectFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [inStoreOnly, setInStoreOnly] = useState(false);

  const IN_STORE_STATUSES = ['Ordered from Vendor', 'Received in Store'];

  useEffect(() => {
    fetchPendingDeliveries();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [items, productFilter, statusFilter, projectFilter, customerFilter, inStoreOnly]);

  const fetchPendingDeliveries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pending-deliveries`, {
        withCredentials: true
      });
      setItems(response.data);
      setFilteredItems(response.data);
    } catch (error) {
      toast.error('Failed to load pending deliveries');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...items];

    if (productFilter) {
      const searchKeywords = productFilter.toLowerCase().trim().split(/\s+/); // Split by spaces
      filtered = filtered.filter(item => {
        const productStr = `${item.product_name} ${item.product_brand} ${item.product_size} ${item.product_color}`.toLowerCase();
        // Check if ALL keywords are present in the product string (in any order)
        return searchKeywords.every(keyword => productStr.includes(keyword));
      });
    }

    if (statusFilter && statusFilter !== 'ALL') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (projectFilter) {
      filtered = filtered.filter(item =>
        item.project_name.toLowerCase().includes(projectFilter.toLowerCase())
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(item =>
        item.customer_name.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }

    if (inStoreOnly) {
      filtered = filtered.filter(item => IN_STORE_STATUSES.includes(item.status));
    }

    setFilteredItems(filtered);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(filteredItems.map(item => item.order_item_id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleBulkUpdate = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    if (!bulkStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/pending-deliveries/bulk-update`,
        {
          order_item_ids: selectedItems,
          new_status: bulkStatus
        },
        { withCredentials: true }
      );
      
      toast.success(`Updated ${response.data.updated_count} items successfully`);
      setSelectedItems([]);
      setBulkStatus('');
      fetchPendingDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update items');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-stone-100 text-stone-800',
      'Delivered': 'bg-lime-100 text-lime-800',
      'Ordered from Vendor': 'bg-blue-100 text-blue-800',
      'Ready for Delivery': 'bg-amber-100 text-amber-800',
      'Dispatched from Vendor': 'bg-amber-100 text-amber-800',
      'Received in Store': 'bg-lime-100 text-lime-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors['Pending']}`}>
        {status}
      </span>
    );
  };

  const statusOptions = [
    'Ordered from Vendor',
    'Ready for Delivery',
    'Received in Store',
    'Delivered',
    'Cancelled'
  ];

  if (loading) {
    return <div className="text-stone-600 text-sm">Loading pending deliveries...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Pending Deliveries
          </h1>
          <p className="text-xs text-stone-600 mt-1">
            {filteredItems.length} items pending • {selectedItems.length} selected
          </p>
        </div>
        <Button
          data-testid="refresh-btn"
          onClick={fetchPendingDeliveries}
          variant="outline"
          size="sm"
          className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs h-8"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Filters & Bulk Actions */}
      <div className="bg-white border border-stone-200 rounded-md p-3 space-y-2">
        {/* Filters Row */}
        <div className="flex gap-2 items-center">
          <Button
            data-testid="in-store-filter-btn"
            onClick={() => setInStoreOnly(!inStoreOnly)}
            size="sm"
            className={`text-xs h-8 whitespace-nowrap ${inStoreOnly ? 'bg-amber-700 hover:bg-amber-800 text-white' : 'bg-white text-stone-700 border border-stone-200 hover:bg-stone-50'}`}
          >
            In Store Pipeline
          </Button>
          <Input
            placeholder="Filter by Product..."
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="text-xs h-8 border-stone-200"
            data-testid="filter-product"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="text-xs h-8 border-stone-200" data-testid="filter-status">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-xs">All Statuses</SelectItem>
              {statusOptions.map(status => (
                <SelectItem key={status} value={status} className="text-xs">{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by Project..."
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="text-xs h-8 border-stone-200"
            data-testid="filter-project"
          />
          <Input
            placeholder="Filter by Customer..."
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="text-xs h-8 border-stone-200"
            data-testid="filter-customer"
          />
        </div>

        {/* Bulk Actions Row */}
        <div className="flex items-center gap-2 pt-2 border-t border-stone-200">
          <span className="text-xs font-medium text-stone-700">Bulk Actions:</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-48 text-xs h-8 border-stone-200" data-testid="bulk-status-select">
              <SelectValue placeholder="Select new status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(status => (
                <SelectItem key={status} value={status} className="text-xs">{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="bulk-update-btn"
            onClick={handleBulkUpdate}
            disabled={selectedItems.length === 0 || !bulkStatus}
            size="sm"
            className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8"
          >
            <Check className="w-3 h-3 mr-1" />
            Update Selected ({selectedItems.length})
          </Button>
          {selectedItems.length > 0 && (
            <Button
              onClick={() => setSelectedItems([])}
              variant="outline"
              size="sm"
              className="text-xs h-8 border-stone-200"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Excel-like Table */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <table className="w-full text-xs border-collapse">
            <thead className="bg-stone-100 sticky top-0 z-10">
              <tr className="border-b border-stone-300">
                <th className="border-r border-stone-300 p-2 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                    onChange={handleSelectAll}
                    className="cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-48">PRODUCT</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-32">CUSTOMER</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-32">PROJECT</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">ORDER ID</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">DATE</th>
                <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-16">QTY</th>
                <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-20">STOCK</th>
                <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-32">REMARKS</th>
                <th className="p-2 text-left font-semibold text-stone-700 w-40">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-6 text-center text-stone-500 text-xs">
                    {items.length === 0 
                      ? 'No pending deliveries. All items are delivered! 🎉'
                      : 'No items match your filters.'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item, index) => (
                  <tr
                    key={item.order_item_id}
                    data-testid={`pending-item-${item.order_item_id}`}
                    className={`border-b border-stone-200 hover:bg-stone-50 ${
                      selectedItems.includes(item.order_item_id) ? 'bg-amber-50' : index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                    }`}
                  >
                    <td className="border-r border-stone-200 p-2 w-8">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.order_item_id)}
                        onChange={() => handleSelectItem(item.order_item_id)}
                        className="cursor-pointer"
                        data-testid={`checkbox-${item.order_item_id}`}
                      />
                    </td>
                    <td className="border-r border-stone-200 p-2 w-48">
                      <div className="font-medium text-stone-900">{item.product_name}</div>
                      <div className="text-stone-600">{item.product_brand} • {item.product_size} • {item.product_color}</div>
                    </td>
                    <td className="border-r border-stone-200 p-2 text-stone-700 w-32">{item.customer_name}</td>
                    <td className="border-r border-stone-200 p-2 text-stone-700 w-32">{item.project_name}</td>
                    <td className="border-r border-stone-200 p-2 text-stone-600 font-mono w-24">{item.order_id}</td>
                    <td className="border-r border-stone-200 p-2 text-stone-700 tabular-nums w-24">{item.order_date}</td>
                    <td className="border-r border-stone-200 p-2 text-right font-medium text-stone-900 tabular-nums w-16">{item.quantity}</td>
                    <td className="border-r border-stone-200 p-2 text-right tabular-nums w-20">
                      <span className={item.available_stock_quantity >= item.quantity ? 'text-lime-700 font-semibold' : 'text-red-700 font-semibold'}>
                        {item.available_stock_quantity}
                      </span>
                    </td>
                    <td className="border-r border-stone-200 p-2 text-stone-600 w-32">{item.remarks || '-'}</td>
                    <td className="p-2 w-40">{getStatusBadge(item.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="bg-stone-50 border border-stone-200 rounded-md p-2 flex justify-between items-center text-xs">
        <div className="text-stone-600">
          Total: <span className="font-semibold text-stone-900">{filteredItems.length}</span> items pending delivery
        </div>
        <div className="text-stone-600">
          Showing filtered results
        </div>
      </div>
    </div>
  );
};

export default PendingDeliveries;
